/**
 * Suggestions API client + local helpers.
 *
 * Backend contract:
 *   GET /api/inventory/{batch_id}/suggestions[?force_refresh=true]
 *      → { ok, batch_id, anchor:{id,name,category},
 *           days_to_expiry, cards:[{kind,title,action,items,availability,confidence}],
 *           generated_at, ttl_seconds, llm_used, llm_configured, cache_hit }
 *
 *   GET /api/suggestions/status
 *      → { enabled: boolean } (always 200, no auth)
 *
 * The 404 returned by the suggestions endpoint when FEATURE_SUGGESTIONS
 * is OFF is intentional and does NOT raise — callers should fall
 * through to "no suggestions".
 */
import api from "./api";

const GOT_IT_KEY_PREFIX = "shelflife.suggestions.got_it.";
const GOT_IT_TTL_MS = 24 * 60 * 60 * 1000;  // 24 hours

// Module-level cache for the server-confirmed feature-enabled flag.
// Resolves once per page load. ``true`` means the server says the
// feature is on; ``false`` means hide the trigger globally. ``null``
// means "not probed yet" — callers should treat as "unknown" and
// optionally hide until resolved (we choose to hide-by-default).
let _featureEnabled = null;          // boolean | null
let _featureProbePromise = null;     // de-dupe in-flight probes

export async function isSuggestionsFeatureEnabled() {
  if (_featureEnabled !== null) return _featureEnabled;
  if (_featureProbePromise) return _featureProbePromise;
  _featureProbePromise = (async () => {
    try {
      const res = await api.get("/suggestions/status");
      _featureEnabled = !!(res?.data?.enabled);
    } catch (_) {
      // If the probe fails (network, 404 in older builds, etc.) hide
      // the trigger — safer to not surface a broken feature.
      _featureEnabled = false;
    }
    return _featureEnabled;
  })();
  return _featureProbePromise;
}

// Test-only reset hook.
export function __resetSuggestionsFeatureCache() {
  _featureEnabled = null;
  _featureProbePromise = null;
}

/**
 * Fetch suggestions for a batch. Returns ``null`` when the feature is
 * dark (404) — callers should treat that as "feature unavailable".
 */
export async function fetchSuggestions(batchId, { forceRefresh = false } = {}) {
  if (!batchId) return null;
  const qs = forceRefresh ? "?force_refresh=true" : "";
  try {
    const res = await api.get(`/inventory/${batchId}/suggestions${qs}`);
    return res?.data || null;
  } catch (err) {
    // 404 = feature dark OR batch not found. Same UX: surface no
    // suggestions rather than an error toast.
    if (err?.response?.status === 404) {
      // If we ever get a 404 from this endpoint, the feature must be
      // off — flip the module flag so subsequent triggers hide
      // themselves on next render without another probe.
      _featureEnabled = false;
      return null;
    }
    throw err;
  }
}

/**
 * Persist a 24-hour "Got it" dismissal for a batch so the trigger
 * disappears from the inventory row until the window expires.
 *
 * Stored shape: ``{ ts: <unix-ms> }`` keyed by batchId. The TTL is
 * checked at read time so an expired key naturally fades out.
 */
export function markGotIt(batchId) {
  if (!batchId) return;
  try {
    localStorage.setItem(
      GOT_IT_KEY_PREFIX + batchId,
      JSON.stringify({ ts: Date.now() }),
    );
  } catch (_) {
    /* localStorage unavailable; degrade silently */
  }
}

export function isGotIt(batchId) {
  if (!batchId) return false;
  try {
    const raw = localStorage.getItem(GOT_IT_KEY_PREFIX + batchId);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    const ts = parsed?.ts;
    if (!ts || typeof ts !== "number") return false;
    if (Date.now() - ts >= GOT_IT_TTL_MS) {
      // expired — proactively clean up so localStorage doesn't bloat
      try { localStorage.removeItem(GOT_IT_KEY_PREFIX + batchId); } catch (_) { /* noop */ }
      return false;
    }
    return true;
  } catch (_) {
    return false;
  }
}

export function clearGotIt(batchId) {
  if (!batchId) return;
  try { localStorage.removeItem(GOT_IT_KEY_PREFIX + batchId); } catch (_) { /* noop */ }
}

export const __TEST_ONLY__ = { GOT_IT_KEY_PREFIX, GOT_IT_TTL_MS };
